import { SimplexNoise } from "./SimplexNoise";
import { Tile } from "./Tile";


// full         
// full_ic_ne   //diags
// full_ic_nw   //diags
// full_ic_se   //diags
// full_ic_sw   //diags
// n            
// e            
// s            
// w            
// ne           
// nw           
// se           
// sw           

export interface TileFactory {
    makeOcean(): Tile;
    makeLand(variant: string): Tile;
    makeTree(): Tile;
}

export class TileGen {

    readonly seed: number;
    readonly width: number;
    readonly height: number;
    readonly scale: number;
    readonly treeChance: number;
    readonly oceanThreshold: number;
    readonly centerBias: number;
    readonly edgeBias: number;
    readonly edgeWaterMargin: number;

    readonly octaves: number;
    readonly persistence: number;
    readonly lacunarity: number;

    readonly maxSanitizePasses: number;

    readonly tileFactory: TileFactory;

    constructor(width: number, height: number, isOverworld: boolean, tileFactory: TileFactory, seed: number = Math.random()) {
        this.seed = seed;
        this.width = width;
        this.height = height;
        this.oceanThreshold = isOverworld ? -.1 : -.34;
        this.treeChance = isOverworld ? .3 : .25;
        this.edgeWaterMargin = isOverworld ? .25 : 0;
        this.centerBias = isOverworld ? .4 : .1;
        this.edgeBias = isOverworld ? .2 : .1;

        this.scale = isOverworld ? .05 : .1;
        this.octaves = isOverworld ? 4 : 3;
        this.persistence = isOverworld ? .5 : .5;
        this.lacunarity = isOverworld ? 2 : 2;

        this.maxSanitizePasses = 8;

        this.tileFactory = tileFactory;
    }

    private fbm(simplex: SimplexNoise, x: number, y: number): number {
        let total = 0;
        let amplitude = 1;
        let frequency = 1;
        let maxAmplitude = 0;

        for (let i = 0; i < this.octaves; i++) {
            total += simplex.noise2D(this.seed * 15671 + x * this.scale * frequency, this.seed * 21059 + y * this.scale * frequency) * amplitude;
            maxAmplitude += amplitude;
            amplitude *= this.persistence;
            frequency *= this.lacunarity;
        }

        // norm
        return total / maxAmplitude;
    }

    private sanitizeLandMask(isLand: boolean[][]): void {
        const land = (x: number, y: number): boolean => {
            if (x < 0 || x >= this.width || y < 0 || y >= this.height) return false;
            return isLand[y][x];
        };

        for (let pass = 0; pass < this.maxSanitizePasses; pass++) {
            const toErode: { x: number; y: number }[] = [];

            for (let y = 0; y < this.height; y++) {
                for (let x = 0; x < this.width; x++) {
                    if (!isLand[y][x]) continue;

                    const waterN = !land(x, y - 1);
                    const waterE = !land(x + 1, y);
                    const waterS = !land(x, y + 1);
                    const waterW = !land(x - 1, y);
                    const waterCount = [waterN, waterE, waterS, waterW].filter(Boolean).length;
                    const isOppositePair = (waterN && waterS) || (waterE && waterW);

                    if (waterCount >= 3 || (waterCount === 2 && isOppositePair)) {
                        toErode.push({ x, y });
                    }
                }
            }

            if (toErode.length === 0) break;
            for (const { x, y } of toErode) {
                isLand[y][x] = false;
            }
        }
    }

    getTiles(): { x: number; y: number; tile: Tile }[] {
        const simplex = new SimplexNoise();
        const treeSimplex = new SimplexNoise();

        const cx = (this.width - 1) / 2;
        const cy = (this.height - 1) / 2;
        const maxDist = Math.sqrt(cx * cx + cy * cy);

        const isLand: boolean[][] = [];
        for (let y = 0; y < this.height; y++) {
            isLand[y] = [];
            for (let x = 0; x < this.width; x++) {
                const value = this.fbm(simplex, x, y);

                const dx = x - cx;
                const dy = y - cy;
                const dist = Math.sqrt(dx * dx + dy * dy) / maxDist;

                const centerPull = this.centerBias * (1 - dist);
                const edgePull = this.edgeBias * dist;
                const adjustedValue = value + centerPull - edgePull;

                const forcedOcean = dist >= 1 - this.edgeWaterMargin;

                isLand[y][x] = !forcedOcean && adjustedValue >= this.oceanThreshold;
            }
        }

        this.sanitizeLandMask(isLand);

        const land = (x: number, y: number): boolean => {
            if (x < 0 || x >= this.width || y < 0 || y >= this.height) return false;
            return isLand[y][x];
        };

        const tiles: { x: number; y: number; tile: Tile }[] = [];

        for (let y = 0; y < this.height; y++) {
            for (let x = 0; x < this.width; x++) {
                if (!isLand[y][x]) {
                    tiles.push({ x, y, tile: this.tileFactory.makeOcean() });
                    continue;
                }

                const waterN = !land(x, y - 1);
                const waterE = !land(x + 1, y);
                const waterS = !land(x, y + 1);
                const waterW = !land(x - 1, y);
                const waterCount = [waterN, waterE, waterS, waterW].filter(Boolean).length;

                let variant: string;

                if (waterCount === 0) {
                    const waterNE = !land(x + 1, y - 1);
                    const waterNW = !land(x - 1, y - 1);
                    const waterSE = !land(x + 1, y + 1);
                    const waterSW = !land(x - 1, y + 1);

                    if (waterNE) variant = "full_ic_ne";
                    else if (waterNW) variant = "full_ic_nw";
                    else if (waterSE) variant = "full_ic_se";
                    else if (waterSW) variant = "full_ic_sw";
                    else variant = "full";
                } else if (waterCount === 1) {
                    if (waterN) variant = "n";
                    else if (waterE) variant = "e";
                    else if (waterS) variant = "s";
                    else variant = "w";
                } else if (waterCount === 2) {
                    if (waterN && waterE) variant = "ne";
                    else if (waterN && waterW) variant = "nw";
                    else if (waterS && waterE) variant = "se";
                    else if (waterS && waterW) variant = "sw";
                    else if (waterN && waterS) variant = "n";
                    else variant = "e";
                } else if (waterCount === 3) {
                    if (!waterN) variant = "se";
                    else if (!waterE) variant = "sw";
                    else if (!waterS) variant = "ne";
                    else variant = "nw";
                } else {
                    variant = "ne";
                }

                if (variant === "full") {
                    const treeValue = treeSimplex.noise2D(this.seed * 49307 + x * this.scale * 2, this.seed * 34981 + y * this.scale * 2);
                    const normalizedTree = (treeValue + 1) / 2;
                    if (normalizedTree < this.treeChance) {
                        tiles.push({ x, y, tile: this.tileFactory.makeTree() });
                        continue;
                    }
                }

                tiles.push({ x, y, tile: this.tileFactory.makeLand(variant) });
            }
        }

        return tiles;
    }
}